import { useEffect, useRef, useState } from "react";

const easeOut = (x) => 1 - Math.pow(1 - x, 3);

/**
 * CountUp — animates a number from 0 up to `target` when scrolled into view.
 *
 * CRITICAL SEO: initial state is `target`, NOT 0 — so build-time prerender
 * (Playwright) and search engines see the final value in the DOM, not "0+".
 * If the component is already in the viewport on mount, or the user has
 * `prefers-reduced-motion`, we skip the animation entirely (no flash).
 * Otherwise on scroll-into-view we reset to 0 and animate up to target.
 */
export const CountUp = ({ target = 0, duration = 1100, suffix = "", className = "", testId }) => {
  const ref = useRef(null);
  const [value, setValue] = useState(target); // ← render target immediately for SSR / prerender / no-JS crawlers
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return; // already showing target
    // During build-time prerender (Playwright / Puppeteer) navigator.webdriver
    // is true. Skip all animation-state mutations so the final target value
    // is captured in the static HTML — search engines and social crawlers
    // see the real number, never "0".
    if (typeof navigator !== "undefined" && navigator.webdriver) return;

    // If element is already inside the viewport on mount, do not animate —
    // showing the correct value immediately is better UX than a 0→target flash.
    const rect = el.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0;
    if (alreadyVisible) return;

    // Otherwise reset to 0 and animate up when scrolled into view.
    setValue(0);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            let t0 = null;
            const step = (ts) => {
              if (!t0) t0 = ts;
              const p = Math.min((ts - t0) / duration, 1);
              const v = easeOut(p) * target;
              setValue(target % 1 === 0 ? Math.round(v) : Math.round(v * 10) / 10);
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.6 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {value.toLocaleString("en-US")}
      {suffix && <span className="u">{suffix}</span>}
    </span>
  );
};

export default CountUp;
